/**
 * SketchService - Manages sketches, entities, and profile detection
 * sketch system with proper boolean region detection
 */

import { Injectable, EventEmitter } from '@angular/core';
import * as clipperLib from 'js-angusj-clipper/web';
import {
  Sketch,
  SketchEntity,
  SketchEntityType,
  SketchProfile,
  SketchConstraint,
  SketchLineEntity,
  SketchCircleEntity,
  SketchArcEntity,
  SketchRectangleEntity,
  SketchPolygonEntity,
  SketchSlotEntity,
  SketchPointEntity,
  ProfileLoop,
  TreeNode
} from '../types';

// Clipper scale factor (clipper uses integers, we use floats)
const CLIPPER_SCALE = 1000;

@Injectable({
  providedIn: 'root'
})
export class SketchService {
  // ═══════════════════════════════════════════════════════════════════════════
  // STATE
  // ═══════════════════════════════════════════════════════════════════════════
  
  private sketches: Map<string, Sketch> = new Map();
  private activeSketchId: string | null = null;
  private selectedEntityIds: Set<string> = new Set();
  private selectedProfileIds: Set<string> = new Set();
  
  // Events
  public onSketchCreated = new EventEmitter<Sketch>();
  public onSketchUpdated = new EventEmitter<Sketch>();
  public onSketchDeleted = new EventEmitter<string>();
  public onEntityCreated = new EventEmitter<SketchEntity>();
  public onEntityUpdated = new EventEmitter<SketchEntity>();
  public onEntityDeleted = new EventEmitter<string>();
  public onSelectionChanged = new EventEmitter<string[]>();
  public onProfilesUpdated = new EventEmitter<SketchProfile[]>();
  public onActiveSketchChanged = new EventEmitter<string | null>();

  // ═══════════════════════════════════════════════════════════════════════════
  // SKETCH MANAGEMENT
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Create a new sketch on a plane
   */
  createSketch(
    plane: 'XY' | 'XZ' | 'YZ' | 'custom',
    name?: string,
    options?: {
      planeOffset?: number;
      planeOrigin?: [number, number, number];
      planeNormal?: [number, number, number];
      planeXAxis?: [number, number, number];
      planeYAxis?: [number, number, number];
      referenceFaceId?: string;
      referenceBodyId?: string;
    }
  ): Sketch {
    const id = this.generateId('sketch');
    
    // Default plane parameters
    let planeOrigin: [number, number, number] = [0, 0, 0];
    let planeNormal: [number, number, number];
    let planeXAxis: [number, number, number];
    let planeYAxis: [number, number, number];
    
    switch (plane) {
      case 'XY':
        planeNormal = [0, 0, 1];
        planeXAxis = [1, 0, 0];
        planeYAxis = [0, 1, 0];
        break;
      case 'XZ':
        planeNormal = [0, 1, 0];
        planeXAxis = [1, 0, 0];
        planeYAxis = [0, 0, 1];
        break;
      case 'YZ':
        planeNormal = [1, 0, 0];
        planeXAxis = [0, 0, 1];
        planeYAxis = [0, 1, 0];
        break;
      case 'custom':
        planeNormal = options?.planeNormal || [0, 0, 1];
        planeXAxis = options?.planeXAxis || [1, 0, 0];
        planeYAxis = options?.planeYAxis || [0, 1, 0];
        break;
    }
    
    if (options?.planeOrigin) planeOrigin = options.planeOrigin;
    
    const sketch: Sketch = {
      id,
      name: name || `Sketch ${this.sketches.size + 1}`,
      plane,
      planeOffset: options?.planeOffset || 0,
      planeOrigin,
      planeNormal,
      planeXAxis,
      planeYAxis,
      entities: [],
      constraints: [],
      profiles: [],
      isActive: false,
      isVisible: true,
      isFullyConstrained: false,
      referenceFaceId: options?.referenceFaceId,
      referenceBodyId: options?.referenceBodyId,
      createdAt: Date.now(),
      updatedAt: Date.now()
    };
    
    this.sketches.set(id, sketch);
    this.onSketchCreated.emit(sketch);
    
    return sketch;
  }

  /**
   * Get a sketch by ID
   */
  getSketch(id: string): Sketch | undefined {
    return this.sketches.get(id);
  }

  /**
   * Get all sketches
   */
  getAllSketches(): Sketch[] {
    return Array.from(this.sketches.values());
  }

  /**
   * Delete a sketch
   */
  deleteSketch(id: string): void {
    if (this.activeSketchId === id) {
      this.setActiveSketch(null);
    }
    this.sketches.delete(id);
    this.onSketchDeleted.emit(id);
  }

  /**
   * Set active sketch (enters sketch edit mode)
   */
  setActiveSketch(id: string | null): void {
    // Deactivate previous
    if (this.activeSketchId) {
      const prev = this.sketches.get(this.activeSketchId);
      if (prev) {
        prev.isActive = false;
      }
    }
    
    this.activeSketchId = id;
    
    // Activate new
    if (id) {
      const sketch = this.sketches.get(id);
      if (sketch) {
        sketch.isActive = true;
      }
    }
    
    this.clearSelection();
    this.onActiveSketchChanged.emit(id);
  }

  /**
   * Get active sketch
   */
  getActiveSketch(): Sketch | null {
    return this.activeSketchId ? this.sketches.get(this.activeSketchId) || null : null;
  }

  /**
   * Rename a sketch
   */
  renameSketch(id: string, name: string): void {
    const sketch = this.sketches.get(id);
    if (sketch) {
      sketch.name = name;
      sketch.updatedAt = Date.now();
      this.onSketchUpdated.emit(sketch);
    }
  }

  /**
   * Set sketch visibility
   */
  setSketchVisibility(id: string, visible: boolean): void {
    const sketch = this.sketches.get(id);
    if (sketch) {
      sketch.isVisible = visible;
      sketch.updatedAt = Date.now();
      this.onSketchUpdated.emit(sketch);
    }
  }

  /**
   * Rename an entity within a sketch
   */
  renameEntity(sketchId: string, entityId: string, name: string): void {
    const sketch = this.sketches.get(sketchId);
    if (!sketch) return;
    
    const entity = sketch.entities.find(e => e.id === entityId);
    if (entity) {
      entity.name = name;
      sketch.updatedAt = Date.now();
      this.onEntityUpdated.emit(entity);
    }
  }

  /**
   * Rename a profile within a sketch
   */
  renameProfile(sketchId: string, profileId: string, name: string): void {
    const sketch = this.sketches.get(sketchId);
    if (!sketch) return;
    
    const profile = sketch.profiles.find(p => p.id === profileId);
    if (profile) {
      profile.name = name;
      sketch.updatedAt = Date.now();
      this.onProfilesUpdated.emit(sketch.profiles);
    }
  }

  /**
   * Duplicate a sketch with all its entities
   */
  duplicateSketch(sketchId: string): Sketch | null {
    const original = this.sketches.get(sketchId);
    if (!original) return null;
    
    // Create new sketch with same plane settings
    const newSketch = this.createSketch(
      original.plane,
      `${original.name} (copy)`,
      {
        planeOffset: original.planeOffset,
        planeOrigin: original.planeOrigin,
        planeNormal: original.planeNormal,
        planeXAxis: original.planeXAxis,
        planeYAxis: original.planeYAxis
      }
    );
    
    // Set as active to add entities
    const previousActiveId = this.activeSketchId;
    this.setActiveSketch(newSketch.id);
    
    // Clone entities (map old IDs to new IDs)
    for (const entity of original.entities) {
      switch (entity.type) {
        case 'line':
          this.createLine(entity.startX, entity.startY, entity.endX, entity.endY, entity.isConstruction);
          break;
        case 'circle':
          this.createCircle(entity.centerX, entity.centerY, entity.radius, entity.isConstruction);
          break;
        case 'rectangle':
          this.createRectangle(entity.x, entity.y, entity.width, entity.height, entity.rotation, entity.isConstruction);
          break;
        case 'arc':
          this.createArc(entity.centerX, entity.centerY, entity.radius, entity.startAngle, entity.endAngle, entity.clockwise, entity.isConstruction);
          break;
        case 'polygon':
          this.createPolygon(entity.centerX, entity.centerY, entity.radius, entity.sides, entity.rotation, entity.isConstruction);
          break;
        case 'slot':
          this.createSlot(entity.startX, entity.startY, entity.endX, entity.endY, entity.width, entity.isConstruction);
          break;
      }
    }
    
    // Restore previous active sketch
    if (previousActiveId) {
      this.setActiveSketch(previousActiveId);
    }
    
    return newSketch;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // ENTITY MANAGEMENT
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Add an entity to the active sketch (internal helper)
   */
  private addEntityToSketch(entity: SketchEntity): SketchEntity | null {
    const sketch = this.getActiveSketch();
    if (!sketch) return null;
    
    sketch.entities.push(entity);
    sketch.updatedAt = Date.now();
    
    // Recalculate profiles
    this.detectProfiles(sketch.id);
    
    this.onEntityCreated.emit(entity);
    return entity;
  }

  /**
   * Create common entity properties
   */
  private createEntityBase(type: SketchEntityType, isConstruction: boolean = false): { id: string; sketchId: string; type: SketchEntityType; isConstruction: boolean; isSelected: boolean; isHovered: boolean; isFullyConstrained: boolean; constraintIds: string[] } {
    const sketch = this.getActiveSketch();
    return {
      id: this.generateId('entity'),
      sketchId: sketch?.id || '',
      type,
      isConstruction,
      isSelected: false,
      isHovered: false,
      isFullyConstrained: false,
      constraintIds: []
    };
  }

  /**
   * Create a line entity
   */
  createLine(startX: number, startY: number, endX: number, endY: number, isConstruction: boolean = false): SketchLineEntity | null {
    const base = this.createEntityBase('line', isConstruction);
    const entity: SketchLineEntity = {
      ...base,
      type: 'line',
      startX, startY, endX, endY
    };
    return this.addEntityToSketch(entity) as SketchLineEntity | null;
  }

  /**
   * Create a circle entity
   */
  createCircle(centerX: number, centerY: number, radius: number, isConstruction: boolean = false): SketchCircleEntity | null {
    const base = this.createEntityBase('circle', isConstruction);
    const entity: SketchCircleEntity = {
      ...base,
      type: 'circle',
      centerX, centerY, radius
    };
    return this.addEntityToSketch(entity) as SketchCircleEntity | null;
  }

  /**
   * Create a rectangle entity
   */
  createRectangle(x: number, y: number, width: number, height: number, rotation: number = 0, isConstruction: boolean = false): SketchRectangleEntity | null {
    const base = this.createEntityBase('rectangle', isConstruction);
    const entity: SketchRectangleEntity = {
      ...base,
      type: 'rectangle',
      x, y, width, height, rotation
    };
    return this.addEntityToSketch(entity) as SketchRectangleEntity | null;
  }

  /**
   * Create a polygon entity
   */
  createPolygon(centerX: number, centerY: number, radius: number, sides: number, rotation: number = 0, isConstruction: boolean = false): SketchPolygonEntity | null {
    const base = this.createEntityBase('polygon', isConstruction);
    const entity: SketchPolygonEntity = {
      ...base,
      type: 'polygon',
      centerX, centerY, radius, sides, rotation
    };
    return this.addEntityToSketch(entity) as SketchPolygonEntity | null;
  }

  /**
   * Create a slot entity
   */
  createSlot(startX: number, startY: number, endX: number, endY: number, width: number, isConstruction: boolean = false): SketchSlotEntity | null {
    const base = this.createEntityBase('slot', isConstruction);
    const entity: SketchSlotEntity = {
      ...base,
      type: 'slot',
      startX, startY, endX, endY, width
    };
    return this.addEntityToSketch(entity) as SketchSlotEntity | null;
  }

  /**
   * Create an arc entity
   */
  createArc(centerX: number, centerY: number, radius: number, startAngle: number, endAngle: number, clockwise: boolean = false, isConstruction: boolean = false): SketchArcEntity | null {
    const base = this.createEntityBase('arc', isConstruction);
    const entity: SketchArcEntity = {
      ...base,
      type: 'arc',
      centerX, centerY, radius, startAngle, endAngle, clockwise
    };
    return this.addEntityToSketch(entity) as SketchArcEntity | null;
  }

  /**
   * Update an entity
   */
  updateEntity(entityId: string, updates: Partial<SketchEntity>): void {
    const sketch = this.getActiveSketch();
    if (!sketch) return;
    
    const index = sketch.entities.findIndex(e => e.id === entityId);
    if (index === -1) return;
    
    sketch.entities[index] = { ...sketch.entities[index], ...updates } as SketchEntity;
    sketch.updatedAt = Date.now();
    
    // Recalculate profiles
    this.detectProfiles(sketch.id);
    
    this.onEntityUpdated.emit(sketch.entities[index]);
  }

  /**
   * Delete an entity
   */
  deleteEntity(entityId: string): void {
    const sketch = this.getActiveSketch();
    if (!sketch) return;
    
    sketch.entities = sketch.entities.filter(e => e.id !== entityId);
    sketch.updatedAt = Date.now();
    
    // Remove from selection
    this.selectedEntityIds.delete(entityId);
    
    // Recalculate profiles
    this.detectProfiles(sketch.id);
    
    this.onEntityDeleted.emit(entityId);
  }

  /**
   * Get entity by ID
   */
  getEntity(sketchId: string, entityId: string): SketchEntity | undefined {
    const sketch = this.sketches.get(sketchId);
    return sketch?.entities.find(e => e.id === entityId);
  }

  /**
   * Move an entity by delta
   */
  moveEntity(entityId: string, deltaX: number, deltaY: number): void {
    const sketch = this.getActiveSketch();
    if (!sketch) return;
    
    const entity = sketch.entities.find(e => e.id === entityId);
    if (!entity) return;
    
    switch (entity.type) {
      case 'point':
        (entity as SketchPointEntity).x += deltaX;
        (entity as SketchPointEntity).y += deltaY;
        break;
      case 'line':
        const line = entity as SketchLineEntity;
        line.startX += deltaX;
        line.startY += deltaY;
        line.endX += deltaX;
        line.endY += deltaY;
        break;
      case 'circle':
        (entity as SketchCircleEntity).centerX += deltaX;
        (entity as SketchCircleEntity).centerY += deltaY;
        break;
      case 'arc':
        (entity as SketchArcEntity).centerX += deltaX;
        (entity as SketchArcEntity).centerY += deltaY;
        break;
      case 'rectangle':
        (entity as SketchRectangleEntity).x += deltaX;
        (entity as SketchRectangleEntity).y += deltaY;
        break;
      case 'polygon':
        (entity as SketchPolygonEntity).centerX += deltaX;
        (entity as SketchPolygonEntity).centerY += deltaY;
        break;
      case 'slot':
        const slot = entity as SketchSlotEntity;
        slot.startX += deltaX;
        slot.startY += deltaY;
        slot.endX += deltaX;
        slot.endY += deltaY;
        break;
    }
    
    sketch.updatedAt = Date.now();
    this.detectProfiles(sketch.id);
    this.onEntityUpdated.emit(entity);
  }

  /**
   * Replace an entity with an updated version (for edit mode)
   */
  replaceEntity(entity: SketchEntity): void {
    const sketch = this.getActiveSketch();
    if (!sketch) return;
    
    const index = sketch.entities.findIndex(e => e.id === entity.id);
    if (index === -1) return;
    
    // Replace the entity with updated one
    sketch.entities[index] = entity;
    sketch.updatedAt = Date.now();
    
    this.onEntityUpdated.emit(entity);
  }

  /**
   * Toggle construction mode for an entity
   */
  toggleEntityConstruction(entityId: string): void {
    const sketch = this.getActiveSketch();
    if (!sketch) return;
    
    const entity = sketch.entities.find(e => e.id === entityId);
    if (!entity) return;
    
    entity.isConstruction = !entity.isConstruction;
    sketch.updatedAt = Date.now();
    
    // Re-detect profiles since construction entities are excluded
    this.detectProfiles(sketch.id);
    
    this.onEntityUpdated.emit(entity);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // SELECTION
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Select an entity
   */
  selectEntity(entityId: string, addToSelection: boolean = false): void {
    const sketch = this.getActiveSketch();
    if (!sketch) return;
    
    if (!addToSelection) {
      // Clear previous selection
      this.clearSelection();
    }
    
    this.selectedEntityIds.add(entityId);
    
    // Update entity state
    const entity = sketch.entities.find(e => e.id === entityId);
    if (entity) {
      entity.isSelected = true;
    }
    
    this.onSelectionChanged.emit(Array.from(this.selectedEntityIds));
  }

  /**
   * Deselect an entity
   */
  deselectEntity(entityId: string): void {
    const sketch = this.getActiveSketch();
    if (!sketch) return;
    
    this.selectedEntityIds.delete(entityId);
    
    const entity = sketch.entities.find(e => e.id === entityId);
    if (entity) {
      entity.isSelected = false;
    }
    
    this.onSelectionChanged.emit(Array.from(this.selectedEntityIds));
  }

  /**
   * Clear all selection
   */
  clearSelection(): void {
    const sketch = this.getActiveSketch();
    if (sketch) {
      sketch.entities.forEach(e => e.isSelected = false);
      sketch.profiles.forEach(p => p.isSelected = false);
    }
    
    this.selectedEntityIds.clear();
    this.selectedProfileIds.clear();
    this.onSelectionChanged.emit([]);
  }

  /**
   * Get selected entity IDs
   */
  getSelectedEntityIds(): string[] {
    return Array.from(this.selectedEntityIds);
  }

  /**
   * Select a profile
   */
  selectProfile(profileId: string, addToSelection: boolean = false): void {
    const sketch = this.getActiveSketch();
    if (!sketch) return;
    
    if (!addToSelection) {
      sketch.profiles.forEach(p => p.isSelected = false);
      this.selectedProfileIds.clear();
    }
    
    this.selectedProfileIds.add(profileId);
    
    const profile = sketch.profiles.find(p => p.id === profileId);
    if (profile) {
      profile.isSelected = true;
    }
  }

  /**
   * Get selected profile IDs
   */
  getSelectedProfileIds(): string[] {
    return Array.from(this.selectedProfileIds);
  }

  /**
   * Get selected profiles
   */
  getSelectedProfiles(): SketchProfile[] {
    const sketch = this.getActiveSketch();
    if (!sketch) return [];
    
    return sketch.profiles.filter(p => this.selectedProfileIds.has(p.id));
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // PROFILE DETECTION (Using Clipper for boolean operations)
  // ═══════════════════════════════════════════════════════════════════════════

  private clipper: clipperLib.ClipperLibWrapper | null = null;

  /**
   * Initialize clipper library
   */
  private async ensureClipper(): Promise<clipperLib.ClipperLibWrapper> {
    if (!this.clipper) {
      this.clipper = await clipperLib.loadNativeClipperLibInstanceAsync(
        clipperLib.NativeClipperLibRequestedFormat.WasmWithAsmJsFallback
      );
    }
    return this.clipper;
  }

  /**
   * Detect closed profiles in a sketch using Clipper boolean operations
   * Uses iterative "shattering" algorithm to decompose overlapping shapes
   * into atomic non-overlapping regions
   */
  async detectProfilesAsync(sketchId: string): Promise<void> {
    const sketch = this.sketches.get(sketchId);
    if (!sketch) return;
    
    // Filter out construction geometry
    const validEntities = sketch.entities.filter(e => !e.isConstruction);
    if (validEntities.length === 0) {
      sketch.profiles = [];
      this.onProfilesUpdated.emit([]);
      return;
    }
    
    // Convert entities to polygons
    const polygons = this.entitiesToPolygons(validEntities);
    const closedPolygons = polygons.filter(p => p.isClosed);
    
    if (closedPolygons.length === 0) {
      sketch.profiles = [];
      this.onProfilesUpdated.emit([]);
      return;
    }
    
    try {
      const clipper = await this.ensureClipper();
      
      // Use the shattering algorithm to get atomic regions
      const atomicRegions = this.shatterPolygons(clipper, closedPolygons, validEntities);
      
      // Convert atomic regions to profiles (including hole information)
      const profiles: SketchProfile[] = atomicRegions.map((region, idx) => {
        // Convert hole points to ProfileLoop format
        const innerLoops: ProfileLoop[] = (region.holes || []).map(holePoints => ({
          entityIds: [],
          isClockwise: this.isClockwise(holePoints),
          points: holePoints
        }));
        
        return this.createProfile(
          sketchId, 
          region.name, 
          region.points, 
          innerLoops, 
          region.sourceEntityIds.length === 1 ? region.sourceEntityIds[0] : undefined
        );
      });
      
      sketch.profiles = profiles;
      this.onProfilesUpdated.emit(profiles);
      
    } catch (error) {
      console.error('Profile detection failed:', error);
      // Fallback to simple detection
      this.detectProfilesSimple(sketchId);
    }
  }

  /**
   * Shatter polygons into atomic non-overlapping regions
   * This implements the iterative decomposition algorithm:
   * 1. Start with first shape as initial region
   * 2. For each new shape, split existing regions by intersection/difference
   * 3. Result is a set of non-overlapping "atomic" regions
   */
  private shatterPolygons(
    clipper: clipperLib.ClipperLibWrapper,
    polygons: Array<{ entityId: string; points: [number, number][]; isClosed: boolean }>,
    entities: SketchEntity[]
  ): Array<{ points: [number, number][]; name: string; sourceEntityIds: string[]; holes: [number, number][][] }> {
    
    // Minimum area threshold to filter out degenerate slivers (in scaled units)
    const MIN_AREA_THRESHOLD = 0.0001;
    
    // Internal representation of an atomic region (can have holes)
    interface AtomicRegion {
      outerPath: clipperLib.ReadonlyPath;
      holePaths: clipperLib.ReadonlyPath[];
      sourceEntityIds: string[];
    }
    
    // Start with empty list of atomic regions
    let atomicRegions: AtomicRegion[] = [];
    
    // Process each polygon one by one
    for (const polygon of polygons) {
      const newPath = this.toClipperPath(polygon.points);
      const newEntityId = polygon.entityId;
      
      if (atomicRegions.length === 0) {
        // First shape - just add it as the initial region (no holes)
        atomicRegions.push({
          outerPath: newPath,
          holePaths: [],
          sourceEntityIds: [newEntityId]
        });
        continue;
      }
      
      // Process the new shape against all existing regions
      const newAtomicRegions: AtomicRegion[] = [];
      // Track remaining pieces of the new shape (as simple paths, no holes yet)
      let remainingNewShape: clipperLib.ReadonlyPath[] = [newPath];
      
      for (let regionIdx = 0; regionIdx < atomicRegions.length; regionIdx++) {
        const existingRegion = atomicRegions[regionIdx];
        
        // Skip if remaining new shape is empty - keep existing region unchanged
        if (remainingNewShape.length === 0) {
          newAtomicRegions.push(existingRegion);
          continue;
        }
        
        // For each piece of the remaining new shape, check against this region
        const nextRemainingNew: clipperLib.ReadonlyPath[] = [];
        let regionWasTouched = false;
        
        for (const newPiece of remainingNewShape) {
          // Calculate intersection (overlap between new and existing outer)
          const intersection = clipper.clipToPaths({
            clipType: clipperLib.ClipType.Intersection,
            subjectInputs: [{ data: existingRegion.outerPath, closed: true }],
            clipInputs: [{ data: newPiece }],
            subjectFillType: clipperLib.PolyFillType.NonZero,
            clipFillType: clipperLib.PolyFillType.NonZero
          });
          
          const hasIntersection = intersection && intersection.length > 0 && 
            intersection.some(p => this.isValidRegion(p, MIN_AREA_THRESHOLD));
          
          if (!hasIntersection) {
            // No overlap with this piece - keep new piece for checking against other regions
            nextRemainingNew.push(newPiece);
            continue;
          }
          
          // Mark this region as touched
          regionWasTouched = true;
          
          // There IS overlap - split into three parts:
          
          // 1. Intersection regions (part of both shapes) - these are simple, no holes
          for (const intPath of intersection) {
            const signedArea = this.calculateSignedAreaClipper(intPath);
            if (signedArea > 0 && this.isValidRegion(intPath, MIN_AREA_THRESHOLD)) {
              newAtomicRegions.push({
                outerPath: intPath,
                holePaths: [],
                sourceEntityIds: [...existingRegion.sourceEntityIds, newEntityId]
              });
            }
          }
          
          // 2. Existing region minus new (part only in existing) - e.g., the "ring"
          // This CAN have holes when inner shape is fully contained
          const existingOnly = clipper.clipToPaths({
            clipType: clipperLib.ClipType.Difference,
            subjectInputs: [{ data: existingRegion.outerPath, closed: true }],
            clipInputs: [{ data: newPiece }],
            subjectFillType: clipperLib.PolyFillType.NonZero,
            clipFillType: clipperLib.PolyFillType.NonZero
          });
          
          if (existingOnly && existingOnly.length > 0) {
            // Separate outer boundaries from hole boundaries
            const outerPaths: clipperLib.ReadonlyPath[] = [];
            const holePaths: clipperLib.ReadonlyPath[] = [];
            
            for (const diffPath of existingOnly) {
              const signedArea = this.calculateSignedAreaClipper(diffPath);
              if (signedArea > 0 && this.isValidRegion(diffPath, MIN_AREA_THRESHOLD)) {
                outerPaths.push(diffPath);
              } else if (signedArea < 0) {
                holePaths.push(diffPath);
              }
            }
            
            // Create region(s) from outer paths with their holes
            // For simple case (1 outer, N holes), create one region
            // For complex case (multiple outers), each gets associated holes
            for (const outerPath of outerPaths) {
              // Find holes that are inside this outer path
              const associatedHoles = holePaths.filter(hole => {
                // Check if hole's first point is inside outer
                if (hole.length > 0) {
                  return this.isPointInClipperPath(hole[0], outerPath);
                }
                return false;
              });
              
              newAtomicRegions.push({
                outerPath: outerPath,
                holePaths: associatedHoles,
                sourceEntityIds: [...existingRegion.sourceEntityIds]
              });
            }
          }
          
          // 3. New piece minus existing (remaining part of new shape)
          const newOnly = clipper.clipToPaths({
            clipType: clipperLib.ClipType.Difference,
            subjectInputs: [{ data: newPiece, closed: true }],
            clipInputs: [{ data: existingRegion.outerPath }],
            subjectFillType: clipperLib.PolyFillType.NonZero,
            clipFillType: clipperLib.PolyFillType.NonZero
          });
          
          if (newOnly && newOnly.length > 0) {
            for (const diffPath of newOnly) {
              const signedArea = this.calculateSignedAreaClipper(diffPath);
              if (signedArea > 0 && this.isValidRegion(diffPath, MIN_AREA_THRESHOLD)) {
                nextRemainingNew.push(diffPath);
              }
            }
          }
        }
        
        // If this region was NOT touched by any new piece, keep it unchanged
        if (!regionWasTouched) {
          newAtomicRegions.push(existingRegion);
        }
        
        remainingNewShape = nextRemainingNew;
      }
      
      // Add any remaining parts of the new shape that didn't overlap with anything
      for (const remaining of remainingNewShape) {
        if (this.isValidRegion(remaining, MIN_AREA_THRESHOLD)) {
          newAtomicRegions.push({
            outerPath: remaining,
            holePaths: [],
            sourceEntityIds: [newEntityId]
          });
        }
      }
      
      atomicRegions = newAtomicRegions;
    }
    
    // Convert atomic regions to output format with proper naming
    return atomicRegions.map(region => {
      const points = this.fromClipperPath(region.outerPath);
      const name = this.generateRegionName(region.sourceEntityIds, entities);
      // Include hole paths as inner loops indicator (for profile creation)
      const holePoints = region.holePaths.map(hp => this.fromClipperPath(hp));
      return {
        points,
        name,
        sourceEntityIds: region.sourceEntityIds,
        holes: holePoints
      };
    });
  }

  /**
   * Check if a point is inside a clipper path
   */
  private isPointInClipperPath(point: clipperLib.IntPoint, path: clipperLib.ReadonlyPath): boolean {
    let inside = false;
    const x = point.x;
    const y = point.y;
    
    for (let i = 0, j = path.length - 1; i < path.length; j = i++) {
      const xi = path[i].x, yi = path[i].y;
      const xj = path[j].x, yj = path[j].y;
      
      if (((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi)) {
        inside = !inside;
      }
    }
    
    return inside;
  }

  /**
   * Calculate signed area of a Clipper path (positive = counter-clockwise, negative = clockwise/hole)
   */
  private calculateSignedAreaClipper(path: clipperLib.ReadonlyPath): number {
    let area = 0;
    for (let i = 0; i < path.length; i++) {
      const j = (i + 1) % path.length;
      area += path[i].x * path[j].y;
      area -= path[j].x * path[i].y;
    }
    return area / 2;
  }

  /**
   * Check if a clipper path represents a valid region (not degenerate)
   */
  private isValidRegion(path: clipperLib.ReadonlyPath, minArea: number): boolean {
    if (path.length < 3) return false;
    
    // Calculate area using shoelace formula on scaled coordinates
    let area = 0;
    for (let i = 0; i < path.length; i++) {
      const j = (i + 1) % path.length;
      area += path[i].x * path[j].y;
      area -= path[j].x * path[i].y;
    }
    area = Math.abs(area) / 2;
    
    // Convert back to unscaled area and compare
    const unscaledArea = area / (CLIPPER_SCALE * CLIPPER_SCALE);
    return unscaledArea >= minArea;
  }

  /**
   * Generate a descriptive name for a region based on source entities
   */
  private generateRegionName(sourceEntityIds: string[], entities: SketchEntity[]): string {
    if (sourceEntityIds.length === 0) return 'Region';
    
    if (sourceEntityIds.length === 1) {
      return this.getEntityName(sourceEntityIds[0], entities);
    }
    
    // For intersections, list the contributing shapes
    const names = sourceEntityIds
      .map(id => this.getEntityName(id, entities))
      .filter((name, idx, arr) => arr.indexOf(name) === idx); // unique names
    
    if (names.length === 1) {
      return `${names[0]} (overlap)`;
    }
    
    return names.join(' ∩ ');
  }

  /**
   * Synchronous wrapper that triggers async detection
   */
  detectProfiles(sketchId: string): void {
    this.detectProfilesAsync(sketchId).catch(err => {
      
      this.detectProfilesSimple(sketchId);
    });
  }

  /**
   * Simple profile detection (fallback without clipper)
   */
  private detectProfilesSimple(sketchId: string): void {
    const sketch = this.sketches.get(sketchId);
    if (!sketch) return;
    
    const validEntities = sketch.entities.filter(e => !e.isConstruction);
    if (validEntities.length === 0) {
      sketch.profiles = [];
      this.onProfilesUpdated.emit([]);
      return;
    }
    
    const profiles: SketchProfile[] = [];
    const polygons = this.entitiesToPolygons(validEntities);
    const closedPolygons = polygons.filter(p => p.isClosed);
    
    // Just create one profile per shape
    for (const poly of closedPolygons) {
      const name = this.getEntityName(poly.entityId, validEntities);
      profiles.push(this.createProfile(sketchId, name, poly.points, [], poly.entityId));
    }
    
    sketch.profiles = profiles;
    this.onProfilesUpdated.emit(profiles);
  }

  /**
   * Helper to create a profile object
   */
  private createProfile(
    sketchId: string,
    name: string,
    points: [number, number][],
    innerLoops: ProfileLoop[],
    entityId?: string  // Track the source entity for circle detection
  ): SketchProfile {
    const outerArea = Math.abs(this.calculateArea(points));
    const innerArea = innerLoops.reduce((sum, loop) => 
      sum + Math.abs(this.calculateArea(loop.points)), 0);
    
    return {
      id: this.generateId('profile'),
      sketchId,
      name,
      outerLoop: {
        entityIds: entityId ? [entityId] : [],
        isClockwise: this.isClockwise(points),
        points
      },
      innerLoops,
      area: outerArea - innerArea,
      centroid: this.calculateCentroid(points),
      boundingBox: this.calculateBoundingBox(points),
      isSelected: false,
      isHovered: false
    };
  }

  /**
   * Convert points to Clipper path (scaled integers)
   */
  private toClipperPath(points: [number, number][]): clipperLib.ReadonlyPath {
    return points.map(p => ({
      x: Math.round(p[0] * CLIPPER_SCALE),
      y: Math.round(p[1] * CLIPPER_SCALE)
    }));
  }

  /**
   * Convert Clipper path back to points
   */
  private fromClipperPath(path: clipperLib.ReadonlyPath): [number, number][] {
    return path.map(p => [p.x / CLIPPER_SCALE, p.y / CLIPPER_SCALE] as [number, number]);
  }
  
  /**
   * Get entity name for profile naming
   */
  private getEntityName(entityId: string, entities: SketchEntity[]): string {
    const entity = entities.find(e => e.id === entityId);
    if (!entity) return 'Profile';
    
    switch (entity.type) {
      case 'circle': return 'Circle';
      case 'rectangle': return 'Rectangle';
      case 'polygon': return 'Polygon';
      case 'slot': return 'Slot';
      case 'arc': return 'Arc';
      default: return 'Shape';
    }
  }

  /**
   * Convert sketch entities to polygon representations
   */
  private entitiesToPolygons(entities: SketchEntity[]): Array<{
    entityId: string;
    points: [number, number][];
    isClosed: boolean;
  }> {
    const polygons: Array<{ entityId: string; points: [number, number][]; isClosed: boolean }> = [];
    
    for (const entity of entities) {
      const points = this.entityToPoints(entity);
      if (points.length > 0) {
        polygons.push({
          entityId: entity.id,
          points,
          isClosed: this.isEntityClosed(entity)
        });
      }
    }
    
    return polygons;
  }

  /**
   * Convert entity to array of points
   */
  entityToPoints(entity: SketchEntity, segments: number = 64): [number, number][] {
    const points: [number, number][] = [];
    
    switch (entity.type) {
      case 'line':
        const line = entity as SketchLineEntity;
        points.push([line.startX, line.startY], [line.endX, line.endY]);
        break;
        
      case 'circle':
        const circle = entity as SketchCircleEntity;
        for (let i = 0; i < segments; i++) {
          const angle = (i / segments) * Math.PI * 2;
          points.push([
            circle.centerX + Math.cos(angle) * circle.radius,
            circle.centerY + Math.sin(angle) * circle.radius
          ]);
        }
        break;
        
      case 'arc':
        const arc = entity as SketchArcEntity;
        const arcAngle = arc.endAngle - arc.startAngle;
        const arcSegments = Math.max(8, Math.ceil(Math.abs(arcAngle) / (Math.PI / 16)));
        for (let i = 0; i <= arcSegments; i++) {
          const t = i / arcSegments;
          const angle = arc.startAngle + arcAngle * t;
          points.push([
            arc.centerX + Math.cos(angle) * arc.radius,
            arc.centerY + Math.sin(angle) * arc.radius
          ]);
        }
        break;
        
      case 'rectangle':
        const rect = entity as SketchRectangleEntity;
        const cos = Math.cos(rect.rotation);
        const sin = Math.sin(rect.rotation);
        const corners = [
          [0, 0],
          [rect.width, 0],
          [rect.width, rect.height],
          [0, rect.height]
        ];
        for (const [cx, cy] of corners) {
          points.push([
            rect.x + cx * cos - cy * sin,
            rect.y + cx * sin + cy * cos
          ]);
        }
        break;
        
      case 'polygon':
        const poly = entity as SketchPolygonEntity;
        for (let i = 0; i < poly.sides; i++) {
          const angle = poly.rotation + (i / poly.sides) * Math.PI * 2 - Math.PI / 2;
          points.push([
            poly.centerX + Math.cos(angle) * poly.radius,
            poly.centerY + Math.sin(angle) * poly.radius
          ]);
        }
        break;
        
      case 'slot':
        const slot = entity as SketchSlotEntity;
        const halfWidth = slot.width / 2;
        const dx = slot.endX - slot.startX;
        const dy = slot.endY - slot.startY;
        const len = Math.sqrt(dx * dx + dy * dy);
        if (len < 0.001) break; // Prevent division by zero
        
        // Perpendicular direction (90 degrees counterclockwise from slot direction)
        const perpX = -dy / len * halfWidth;
        const perpY = dx / len * halfWidth;
        const slotAngle = Math.atan2(dy, dx);
        
        // Create slot shape going counterclockwise (standard winding)
        // Start at left side of start point
        points.push([slot.startX + perpX, slot.startY + perpY]);
        
        // Go to left side of end point
        points.push([slot.endX + perpX, slot.endY + perpY]);
        
        // End cap (semicircle going counterclockwise from left to right)
        for (let i = 1; i <= 32; i++) {
          const t = i / 32;
          const angle = slotAngle + Math.PI / 2 - t * Math.PI;
          points.push([
            slot.endX + Math.cos(angle) * halfWidth,
            slot.endY + Math.sin(angle) * halfWidth
          ]);
        }
        
        // Go to right side of start point
        points.push([slot.startX - perpX, slot.startY - perpY]);
        
        // Start cap (semicircle going counterclockwise from right to left)
        for (let i = 1; i <= 32; i++) {
          const t = i / 32;
          const angle = slotAngle - Math.PI / 2 - t * Math.PI;
          points.push([
            slot.startX + Math.cos(angle) * halfWidth,
            slot.startY + Math.sin(angle) * halfWidth
          ]);
        }
        break;
    }
    
    return points;
  }

  /**
   * Check if entity forms a closed shape
   */
  private isEntityClosed(entity: SketchEntity): boolean {
    return ['circle', 'rectangle', 'polygon', 'slot'].includes(entity.type);
  }

  /**
   * Check if polygon A is inside polygon B
   */
  private isPolygonInside(polyA: [number, number][], polyB: [number, number][]): boolean {
    // Check if all points of polygon A are inside polygon B
    for (const point of polyA) {
      if (!this.isPointInPolygon(point, polyB)) {
        return false;
      }
    }
    return true;
  }

  /**
   * Check if two polygons overlap (intersect but neither fully contains the other)
   */
  private doPolygonsOverlap(polyA: [number, number][], polyB: [number, number][]): boolean {
    // Quick bounding box check first
    const boundsA = this.calculateBoundingBox(polyA);
    const boundsB = this.calculateBoundingBox(polyB);
    
    // If bounding boxes don't overlap, polygons can't overlap
    if (boundsA.maxX < boundsB.minX || boundsB.maxX < boundsA.minX ||
        boundsA.maxY < boundsB.minY || boundsB.maxY < boundsA.minY) {
      return false;
    }
    
    // Check if any point of A is inside B (but not ALL points)
    let aPointsInsideB = 0;
    for (const point of polyA) {
      if (this.isPointInPolygon(point, polyB)) {
        aPointsInsideB++;
      }
    }
    
    // Check if any point of B is inside A (but not ALL points)
    let bPointsInsideA = 0;
    for (const point of polyB) {
      if (this.isPointInPolygon(point, polyA)) {
        bPointsInsideA++;
      }
    }
    
    // Overlap occurs when:
    // - Some (but not all) points of A are in B, OR
    // - Some (but not all) points of B are in A
    const someAinB = aPointsInsideB > 0 && aPointsInsideB < polyA.length;
    const someBinA = bPointsInsideA > 0 && bPointsInsideA < polyB.length;
    
    return someAinB || someBinA;
  }

  /**
   * Check if point is inside polygon using ray casting
   */
  private isPointInPolygon(point: [number, number], polygon: [number, number][]): boolean {
    const [x, y] = point;
    let inside = false;
    
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const [xi, yi] = polygon[i];
      const [xj, yj] = polygon[j];
      
      if (((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi)) {
        inside = !inside;
      }
    }
    
    return inside;
  }

  /**
   * Check if polygon is clockwise
   */
  private isClockwise(points: [number, number][]): boolean {
    let sum = 0;
    for (let i = 0; i < points.length; i++) {
      const [x1, y1] = points[i];
      const [x2, y2] = points[(i + 1) % points.length];
      sum += (x2 - x1) * (y2 + y1);
    }
    return sum > 0;
  }

  /**
   * Calculate polygon area using shoelace formula
   */
  private calculateArea(points: [number, number][]): number {
    let area = 0;
    for (let i = 0; i < points.length; i++) {
      const [x1, y1] = points[i];
      const [x2, y2] = points[(i + 1) % points.length];
      area += x1 * y2 - x2 * y1;
    }
    return Math.abs(area / 2);
  }

  /**
   * Calculate polygon centroid
   */
  private calculateCentroid(points: [number, number][]): { x: number; y: number } {
    let cx = 0, cy = 0;
    for (const [x, y] of points) {
      cx += x;
      cy += y;
    }
    return {
      x: cx / points.length,
      y: cy / points.length
    };
  }

  /**
   * Calculate bounding box
   */
  private calculateBoundingBox(points: [number, number][]): { minX: number; minY: number; maxX: number; maxY: number } {
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const [x, y] of points) {
      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x);
      maxY = Math.max(maxY, y);
    }
    return { minX, minY, maxX, maxY };
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // TREE VIEW
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Build tree view structure
   */
  buildTreeView(): TreeNode[] {
    const nodes: TreeNode[] = [];
    
    for (const sketch of this.sketches.values()) {
      const sketchNode: TreeNode = {
        id: `tree-${sketch.id}`,
        name: sketch.name,
        type: 'sketch',
        icon: '📐',
        isExpanded: sketch.isActive,
        isSelected: sketch.isActive,
        isVisible: sketch.isVisible,
        isEditing: false,
        parentId: null,
        children: [],
        dataId: sketch.id,
        dataType: 'sketch'
      };
      
      // Create "Entities" folder
      if (sketch.entities.length > 0) {
        const entitiesFolder: TreeNode = {
          id: `tree-entities-${sketch.id}`,
          name: `Shapes (${sketch.entities.length})`,
          type: 'folder',
          icon: '📁',
          isExpanded: sketch.isActive,
          isSelected: false,
          isVisible: true,
          isEditing: false,
          parentId: sketchNode.id,
          children: [],
          dataId: `entities-${sketch.id}`,
          dataType: 'folder'
        };
        
        // Add entity children
        for (const entity of sketch.entities) {
          const entityNode: TreeNode = {
            id: `tree-${entity.id}`,
            name: this.getEntityDisplayName(entity),
            type: 'entity',
            icon: this.getEntityIcon(entity.type),
            isExpanded: false,
            isSelected: entity.isSelected,
            isVisible: true,
            isEditing: false,
            parentId: entitiesFolder.id,
            children: [],
            dataId: entity.id,
            dataType: 'entity',
            metadata: {
              isConstruction: entity.isConstruction,
              entityType: entity.type
            }
          };
          entitiesFolder.children.push(entityNode);
        }
        
        sketchNode.children.push(entitiesFolder);
      }
      
      // Create "Profiles" folder
      if (sketch.profiles.length > 0) {
        const profilesFolder: TreeNode = {
          id: `tree-profiles-${sketch.id}`,
          name: `Profiles (${sketch.profiles.length})`,
          type: 'folder',
          icon: '📁',
          isExpanded: true,
          isSelected: false,
          isVisible: true,
          isEditing: false,
          parentId: sketchNode.id,
          children: [],
          dataId: `profiles-${sketch.id}`,
          dataType: 'folder'
        };
        
        // Add profile children
        for (const profile of sketch.profiles) {
          const profileNode: TreeNode = {
            id: `tree-${profile.id}`,
            name: profile.name || 'Profile',
            type: 'profile',
            icon: profile.innerLoops.length > 0 ? '◯' : '⬜',
            isExpanded: false,
            isSelected: profile.isSelected,
            isVisible: true,
            isEditing: false,
            parentId: profilesFolder.id,
            children: [],
            dataId: profile.id,
            dataType: 'profile',
            metadata: {
              area: profile.area,
              hasHoles: profile.innerLoops.length > 0
            }
          };
          profilesFolder.children.push(profileNode);
        }
        
        sketchNode.children.push(profilesFolder);
      }
      
      nodes.push(sketchNode);
    }
    
    return nodes;
  }

  /**
   * Get display name for entity
   */
  private getEntityDisplayName(entity: SketchEntity): string {
    // Use custom name if set
    if (entity.name) {
      return entity.name;
    }
    
    // Generate default name based on type and dimensions (rounded to 2 decimals)
    switch (entity.type) {
      case 'line': return 'Line';
      case 'circle': 
        const circle = entity as SketchCircleEntity;
        return `Circle (⌀${(circle.radius * 2).toFixed(2)})`;
      case 'arc': return 'Arc';
      case 'rectangle':
        const rect = entity as SketchRectangleEntity;
        return `Rectangle (${rect.width.toFixed(2)}×${rect.height.toFixed(2)})`;
      case 'polygon':
        const poly = entity as SketchPolygonEntity;
        return `${poly.sides}-gon`;
      case 'slot': return 'Slot';
      case 'point': return 'Point';
      default: return 'Entity';
    }
  }

  /**
   * Get icon for entity type
   */
  private getEntityIcon(type: SketchEntityType): string {
    switch (type) {
      case 'line': return '📏';
      case 'circle': return '⚪';
      case 'arc': return '↷';
      case 'rectangle': return '🔲';
      case 'polygon': return '⬡';
      case 'slot': return '💊';
      case 'point': return '•';
      default: return '?';
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // UTILITIES
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Generate unique ID
   */
  private generateId(prefix: string): string {
    return `${prefix}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Clear all data
   */
  clear(): void {
    this.sketches.clear();
    this.activeSketchId = null;
    this.selectedEntityIds.clear();
    this.selectedProfileIds.clear();
  }

  /**
   * Export sketch to JSON
   */
  exportSketch(sketchId: string): string | null {
    const sketch = this.sketches.get(sketchId);
    if (!sketch) return null;
    return JSON.stringify(sketch, null, 2);
  }

  /**
   * Import sketch from JSON
   */
  importSketch(json: string): Sketch | null {
    try {
      const sketch = JSON.parse(json) as Sketch;
      sketch.id = this.generateId('sketch');
      sketch.entities.forEach(e => {
        e.id = this.generateId('entity');
        e.sketchId = sketch.id;
      });
      this.sketches.set(sketch.id, sketch);
      this.detectProfiles(sketch.id);
      this.onSketchCreated.emit(sketch);
      return sketch;
    } catch (e) {
      
      return null;
    }
  }

  /**
   * Get profiles for extrusion (returns 2D points arrays)
   */
  getProfilesForExtrusion(sketchId: string, profileIds: string[]): Array<{
    outer: number[][];
    holes: number[][][];
    isCircle: boolean;
    isRing: boolean;
    isSlot: boolean;
    circleData?: { centerX: number; centerY: number; radius: number };
    ringData?: { centerX: number; centerY: number; outerRadius: number; innerRadius: number };
    slotData?: { startX: number; startY: number; endX: number; endY: number; width: number };
    // For frames with circle holes (polygon outer + circle inner)
    circleHoles?: Array<{ centerX: number; centerY: number; radius: number }>;
  }> {
    const sketch = this.sketches.get(sketchId);
    if (!sketch) return [];

    const result: Array<{
      outer: number[][];
      holes: number[][][];
      isCircle: boolean;
      isRing: boolean;
      isSlot: boolean;
      circleData?: { centerX: number; centerY: number; radius: number };
      ringData?: { centerX: number; centerY: number; outerRadius: number; innerRadius: number };
      slotData?: { startX: number; startY: number; endX: number; endY: number; width: number };
      circleHoles?: Array<{ centerX: number; centerY: number; radius: number }>;
    }> = [];
    
    for (const profileId of profileIds) {
      const profile = sketch.profiles.find(p => p.id === profileId);
      if (!profile) continue;
      
      let isCircle = false;
      let isRing = false;
      let isSlot = false;
      let circleData: { centerX: number; centerY: number; radius: number } | undefined;
      let ringData: { centerX: number; centerY: number; outerRadius: number; innerRadius: number } | undefined;
      let slotData: { startX: number; startY: number; endX: number; endY: number; width: number } | undefined;
      const circleHoles: Array<{ centerX: number; centerY: number; radius: number }> = [];
      const nonCircleHoles: number[][][] = [];

      // Try to detect circle from geometry first, then fall back to entity lookup
      const outerCircleData = this.detectCircleFromPoints(profile.outerLoop.points) ||
                              this.getCircleDataFromEntity(profile.outerLoop.entityIds, sketch.entities);

      // Try to detect slot from entity
      const outerSlotData = this.getSlotDataFromEntity(profile.outerLoop.entityIds, sketch.entities);
      
      // Check each inner loop for circles (geometry-based detection)
      for (const innerLoop of profile.innerLoops) {
        const innerCircleData = this.detectCircleFromPoints(innerLoop.points) ||
                                this.getCircleDataFromEntities(innerLoop.entityIds, sketch.entities);
        
        if (innerCircleData) {
          circleHoles.push(innerCircleData);
        } else {
          // Non-circle hole - keep as polygon
          nonCircleHoles.push(innerLoop.points.map(p => [p[0], p[1]]));
        }
      }
      
      // Determine the profile type
      if (outerCircleData) {
        if (profile.innerLoops.length === 0) {
          // Pure circle - no holes
          isCircle = true;
          circleData = outerCircleData;
        } else if (circleHoles.length === 1 && nonCircleHoles.length === 0) {
          // Check if it's a concentric ring
          const innerHole = circleHoles[0];
          const centerDist = Math.sqrt(
            Math.pow(outerCircleData.centerX - innerHole.centerX, 2) +
            Math.pow(outerCircleData.centerY - innerHole.centerY, 2)
          );

          if (centerDist < 0.5) { // Approximately concentric (increased tolerance)
            isRing = true;
            ringData = {
              centerX: outerCircleData.centerX,
              centerY: outerCircleData.centerY,
              outerRadius: outerCircleData.radius,
              innerRadius: innerHole.radius
            };
          } else {
            // Circle outer with non-concentric circle hole(s)
            isCircle = true;
            circleData = outerCircleData;
          }
        } else {
          // Circle outer with multiple holes or mixed holes
          isCircle = true;
          circleData = outerCircleData;
        }
      } else if (outerSlotData && profile.innerLoops.length === 0) {
        // Pure slot - no holes (slots with holes fall back to polygon extrusion)
        isSlot = true;
        slotData = outerSlotData;
      } else {
        // Outer is not a circle or slot - check if any inner holes are circles
        // (they'll be subtracted as cylinders)
      }

      result.push({
        outer: profile.outerLoop.points.map(p => [p[0], p[1]]),
        holes: nonCircleHoles, // Only non-circle holes as polygon points
        isCircle,
        isRing,
        isSlot,
        circleData,
        ringData,
        slotData,
        circleHoles: circleHoles.length > 0 && !isRing ? circleHoles : undefined
      });
    }
    
    return result;
  }

  /**
   * Detect if points form a circle by checking if all points are equidistant from center
   */
  private detectCircleFromPoints(points: [number, number][]): { centerX: number; centerY: number; radius: number } | null {
    if (points.length < 8) return null; // Need enough points to reliably detect circle
    
    // Calculate centroid as potential center
    let sumX = 0, sumY = 0;
    for (const [x, y] of points) {
      sumX += x;
      sumY += y;
    }
    const centerX = sumX / points.length;
    const centerY = sumY / points.length;
    
    // Calculate distances from center
    const distances: number[] = [];
    for (const [x, y] of points) {
      const dist = Math.sqrt((x - centerX) ** 2 + (y - centerY) ** 2);
      distances.push(dist);
    }
    
    // Calculate average radius and standard deviation
    const avgRadius = distances.reduce((a, b) => a + b, 0) / distances.length;
    const variance = distances.reduce((sum, d) => sum + (d - avgRadius) ** 2, 0) / distances.length;
    const stdDev = Math.sqrt(variance);
    
    // If standard deviation is very small relative to radius, it's a circle
    // Allow 1% variation
    const tolerance = avgRadius * 0.01;
    
    if (stdDev < tolerance && avgRadius > 1) {
      return { centerX, centerY, radius: avgRadius };
    }
    
    return null;
  }

  /**
   * Get circle data from a single entity ID
   */
  private getCircleDataFromEntity(entityIds: string[], entities: SketchEntity[]): { centerX: number; centerY: number; radius: number } | null {
    if (entityIds.length !== 1) return null;
    
    const entity = entities.find(e => e.id === entityIds[0]);
    if (entity?.type === 'circle') {
      const circle = entity as SketchCircleEntity;
      return {
        centerX: circle.centerX,
        centerY: circle.centerY,
        radius: circle.radius
      };
    }
    return null;
  }

  /**
   * Get circle data from entity IDs (for inner loops which may have multiple entity IDs)
   */
  private getCircleDataFromEntities(entityIds: string[], entities: SketchEntity[]): { centerX: number; centerY: number; radius: number } | null {
    // If single entity, check if it's a circle
    if (entityIds.length === 1) {
      return this.getCircleDataFromEntity(entityIds, entities);
    }
    
    // If multiple entities, check if all are the same circle (shouldn't happen, but just in case)
    // Or find any circle entity in the list
    for (const entityId of entityIds) {
      const entity = entities.find(e => e.id === entityId);
      if (entity?.type === 'circle') {
        const circle = entity as SketchCircleEntity;
        return {
          centerX: circle.centerX,
          centerY: circle.centerY,
          radius: circle.radius
        };
      }
    }

    return null;
  }

  /**
   * Get slot data from entity IDs
   */
  private getSlotDataFromEntity(entityIds: string[], entities: SketchEntity[]): { startX: number; startY: number; endX: number; endY: number; width: number } | null {
    if (entityIds.length !== 1) return null;

    const entity = entities.find(e => e.id === entityIds[0]);
    if (entity?.type === 'slot') {
      const slot = entity as SketchSlotEntity;
      return {
        startX: slot.startX,
        startY: slot.startY,
        endX: slot.endX,
        endY: slot.endY,
        width: slot.width
      };
    }
    return null;
  }
}

